<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterView, useRouter, useRoute, RouterLink } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  Bell,
  Loading,
  CaretBottom,
  ArrowDown,
  SwitchButton,
  User,
  Search,
  Expand,
} from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useSyncStatusStore } from '@/stores/sync-status';
import { setLocale, type Locale } from '@/i18n';
import { filterNavSectionsByFeatures, navSections, type NavItem } from './nav';
import { useIsMobile } from '@/composables/useIsMobile';
import SidebarNav from './SidebarNav.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import ShortcutHelp from '@/components/ShortcutHelp.vue';

const auth = useAuthStore();
const notifications = useNotificationsStore();
const syncStatus = useSyncStatusStore();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();

function onLocaleChange(l: Locale) {
  setLocale(l);
}

// `navSections` + its types now live in ./nav as the single source of truth so
// the CommandPalette can consume the same registry the sidebar renders. The
// accordion open-state + route-sync logic now lives in SidebarNav (the shared
// brand+nav+foot rendered by both the desktop aside and the mobile drawer).

const visibleNavSections = computed(() =>
  filterNavSectionsByFeatures(navSections, auth.hasFeature),
);
const canUseNotifications = computed(() => auth.hasFeature('monitor.notifications'));
const canUseTasks = computed(() => auth.hasFeature('monitor.tasks'));

onMounted(() => {
  if (canUseNotifications.value) notifications.startPolling();
  if (canUseTasks.value) syncStatus.startPolling();
});

onBeforeUnmount(() => {
  notifications.stopPolling();
  syncStatus.stopPolling();
});

watch(canUseNotifications, (enabled) => {
  if (enabled) notifications.startPolling();
  else notifications.stopPolling();
});

watch(canUseTasks, (enabled) => {
  if (enabled) syncStatus.startPolling();
  else syncStatus.stopPolling();
});

const allItems = computed(() =>
  visibleNavSections.value.flatMap((s) => [...(s.entry ? [s.entry] : []), ...s.items]),
);

function itemTarget(item: NavItem) {
  return item.to ?? { name: item.name };
}

function targetName(item: NavItem) {
  const target = itemTarget(item);
  return target && typeof target === 'object' && 'name' in target ? target.name : item.name;
}

function queryValueMatches(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(actual)) return actual[0] === String(expected);
  return actual === String(expected);
}

function hasTargetQuery(item: NavItem): boolean {
  const target = itemTarget(item);
  return !!(target && typeof target === 'object' && 'query' in target && target.query && Object.keys(target.query).length);
}

function isSpecificItemActive(item: NavItem): boolean {
  const target = itemTarget(item);
  if (!target || typeof target !== 'object' || !('name' in target)) return false;
  if (target.name !== route.name || !('query' in target) || !target.query) return false;
  const entries = Object.entries(target.query);
  return entries.length > 0 && entries.every(([key, value]) => queryValueMatches(route.query[key], value));
}

const currentTitle = computed(() => {
  const specific = allItems.value.find(isSpecificItemActive);
  if (specific) return t(specific.label);
  const item = allItems.value.find((i) => !hasTargetQuery(i) && targetName(i) === route.name);
  return item ? t(item.label) : (route.meta.title as string) || '';
});

async function onLogout() {
  // Wait one tick so the dropdown menu finishes its teleport teardown before
  // ElMessageBox mounts. Without this the confirm box can inherit the
  // dropdown's z-index / transform context and render misaligned.
  await nextTick();
  try {
    await ElMessageBox.confirm(t('layout.logout_confirm'), t('common.prompt_title'), {
      type: 'warning',
      confirmButtonText: t('layout.logout_confirm_btn'),
      cancelButtonText: t('common.cancel'),
    });
  } catch {
    return;
  }
  try {
    await auth.logout();
    router.push({ name: 'internal-login', query: { logged_out: '1' } });
  } catch {
    ElMessage.error('退出未完成，请稍后重试。');
  }
}

function onProfile() {
  router.push({ name: 'settings' });
}

// --- Command palette + global keyboard shortcuts ---------------------------
const palette = ref<InstanceType<typeof CommandPalette> | null>(null);
const shortcutHelp = ref<InstanceType<typeof ShortcutHelp> | null>(null);

/** True when the user is typing in a field — used to gate the `?` shortcut so
 *  we never hijack a literal "?" keystroke in an input. */
function isEditableTarget(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
}

function onGlobalKeydown(e: KeyboardEvent) {
  // ⌘K / Ctrl+K — opens the palette everywhere (standard, works inside inputs).
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault();
    shortcutHelp.value?.close(); // never stack the palette on top of the help overlay
    palette.value?.open();
    return;
  }
  // `?` (Shift+/) — help, but only outside text fields.
  if (e.key === '?' && !e.altKey && !e.metaKey && !e.ctrlKey && !isEditableTarget()) {
    e.preventDefault();
    shortcutHelp.value?.open();
  }
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKeydown));

const userInitial = computed(() => {
  const n = auth.user?.name || auth.user?.email || '?';
  return n.charAt(0).toUpperCase();
});

// --- Mobile shell ----------------------------------------------------------
// On phones/tablets (≤768px) the fixed left sidebar would eat the screen, so it
// collapses into an el-drawer toggled by a hamburger in the header. `isMobile`
// is the single source of truth (matchMedia) shared with the rest of the app.
const isMobile = useIsMobile();
const drawerOpen = ref(false);

// Close the drawer whenever the route changes so tapping a nav item navigates
// AND dismisses the overlay (otherwise the drawer would stay open over the page).
watch(
  () => route.name,
  () => {
    drawerOpen.value = false;
  },
);
</script>

<template>
  <div class="app-shell">
    <!-- Desktop sidebar — hidden ≤768px via CSS, replaced by the drawer. -->
    <aside class="app-sidebar">
      <SidebarNav :nav-sections="visibleNavSections" />
    </aside>

    <!-- Mobile drawer — same nav, toggled by the header hamburger. -->
    <el-drawer
      v-model="drawerOpen"
      direction="ltr"
      :with-header="false"
      size="260px"
      class="app-drawer"
    >
      <div class="app-sidebar app-sidebar--drawer">
        <SidebarNav :nav-sections="visibleNavSections" />
      </div>
    </el-drawer>

    <div class="app-main">
      <header class="app-header">
        <div class="app-header__crumbs">
          <button
            v-if="isMobile"
            type="button"
            class="hamburger"
            :aria-label="t('layout.open_menu')"
            :title="t('layout.open_menu')"
            @click="drawerOpen = true"
          >
            <el-icon :size="20"><Expand /></el-icon>
          </button>
          <span class="app-header__page">{{ currentTitle }}</span>
        </div>
        <div class="app-header__right">
          <button
            type="button"
            class="cmdk-hint"
            :title="t('commandPalette.placeholder')"
            @click="palette?.open()"
          >
            <el-icon :size="14"><Search /></el-icon>
            <kbd>⌘K</kbd>
          </button>
          <RouterLink
            v-if="canUseTasks"
            :to="{ name: 'tasks' }"
            class="header-icon"
            :class="{ 'header-icon--active': syncStatus.pending > 0 }"
            :title="
              syncStatus.pending > 0
                ? t('layout.tasks_running', { count: syncStatus.pending })
                : t('nav.tasks')
            "
          >
            <el-badge
              :value="syncStatus.pending"
              :hidden="syncStatus.pending === 0"
              :max="9"
              type="primary"
            >
              <el-icon :size="18" :class="{ 'header-icon__spin': syncStatus.pending > 0 }">
                <Loading />
              </el-icon>
            </el-badge>
            <span
              v-if="syncStatus.pending === 0 && syncStatus.recentFailed > 0"
              class="header-icon__warn-dot"
              :title="t('layout.tasks_failed', { count: syncStatus.recentFailed })"
            ></span>
          </RouterLink>
          <RouterLink
            v-if="canUseNotifications"
            :to="{ name: 'notifications' }"
            class="header-icon"
            :title="t('nav.notifications')"
          >
            <el-badge
              :value="notifications.unreadCount"
              :hidden="notifications.unreadCount === 0"
              :max="9"
            >
              <el-icon :size="18"><Bell /></el-icon>
            </el-badge>
          </RouterLink>
          <el-dropdown
            trigger="click"
            @command="(cmd: string) => {
              if (cmd === 'logout') onLogout();
              else if (cmd === 'profile') onProfile();
              else if (cmd === 'lang_zh') onLocaleChange('zh-CN');
              else if (cmd === 'lang_en') onLocaleChange('en-US');
            }"
          >
            <div class="user-trigger">
              <div class="user-avatar">{{ userInitial }}</div>
            <span class="user-name">{{ auth.user?.name }}</span>
            <el-icon :size="14"><CaretBottom /></el-icon>
          </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>
                  <span>{{ $t('nav.profile') }}</span>
                </el-dropdown-item>
                <el-dropdown-item command="lang_zh" :disabled="locale === 'zh-CN'" divided>
                  <span>中文</span>
                </el-dropdown-item>
                <el-dropdown-item command="lang_en" :disabled="locale === 'en-US'">
                  <span>English</span>
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon>
                  <span>{{ $t('nav.logout') }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>
      <!-- Global sync banner — polled by useSyncStatusStore every 15s -->
      <div v-if="canUseTasks && syncStatus.hasActivity" class="sync-banner">
        <span class="sync-banner__dot"></span>
        <span>
          {{ $t('layout.sync_in_progress', { count: syncStatus.pending }) }}
          <span v-if="syncStatus.recentFailed > 0" class="sync-banner__warn">
            · {{ $t('layout.sync_recent_failed', { count: syncStatus.recentFailed }) }}
          </span>
        </span>
        <RouterLink v-if="canUseTasks" :to="{ name: 'tasks' }" class="sync-banner__link">
          {{ $t('layout.view_progress') }} →
        </RouterLink>
      </div>
      <main class="app-content">
        <RouterView />
      </main>
    </div>

    <CommandPalette ref="palette" :nav-sections="visibleNavSections" />
    <ShortcutHelp ref="shortcutHelp" />
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* ---------------- Sidebar ---------------- */
.app-sidebar {
  width: 232px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
}
.app-sidebar__brand {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  border-bottom: 1px solid #e2e8f0;
  text-decoration: none;
}
.app-sidebar__logo {
  height: 28px;
  width: auto;
  display: block;
}
.app-sidebar__nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
  padding: 16px 12px 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s;
}
.nav-section-title:hover {
  color: #2563eb;
}
.nav-section-title__chevron {
  transition: transform 0.18s ease;
  opacity: 0.6;
}
.nav-section-title--collapsed .nav-section-title__chevron {
  transform: rotate(-90deg);
}
.nav-section-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  text-decoration: none;
  font-weight: 500;
  transition: background 0.12s, color 0.12s;
  cursor: pointer;
}
.nav-item:hover {
  background: #f1f5f9;
  color: #0f172a;
}
.nav-item__icon {
  color: #94a3b8;
  transition: color 0.12s;
}
.nav-item:hover .nav-item__icon {
  color: #475569;
}
.nav-item--active {
  background: #eff6ff;
  color: #2563eb;
}
.nav-item--active .nav-item__icon {
  color: #2563eb;
}
.app-sidebar__foot {
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
}
.app-sidebar__version {
  font-size: 11px;
  color: #94a3b8;
}

/* The drawer wrapper reuses .app-sidebar for identical nav styling, but inside
 * the el-drawer it must not be a sticky 100vh column — it fills the drawer. */
.app-sidebar--drawer {
  position: static;
  height: 100%;
  width: 100%;
  border-right: none;
}

/* ---------------- Hamburger (mobile only) ---------------- */
.app-header__crumbs {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.hamburger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;
}
.hamburger:hover {
  background: #f1f5f9;
  color: #0f172a;
}

/* ---------------- Main column ---------------- */
.app-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 24px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}
.app-header__page {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.app-content {
  flex: 1;
  padding: 24px 28px;
  min-width: 0;
}
.sync-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 28px;
  background: linear-gradient(90deg, #eff6ff 0%, #f5f3ff 100%);
  border-bottom: 1px solid #c7d2fe;
  font-size: 13px;
  color: #1e1b4b;
}
.sync-banner strong {
  font-weight: 600;
}
.sync-banner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6366f1;
  animation: sync-pulse 1.2s ease-in-out infinite;
}
.sync-banner__warn {
  color: #b45309;
}
.sync-banner__link {
  margin-left: auto;
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  font-size: 12px;
}
.sync-banner__link:hover {
  text-decoration: underline;
}
@keyframes sync-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

/* ---------------- Header right ---------------- */
.app-header__right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.cmdk-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.cmdk-hint:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #cbd5e1;
}
.cmdk-hint kbd {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #64748b;
}
.header-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.header-icon:hover {
  background: #f1f5f9;
  color: #0f172a;
}
.header-icon--active {
  color: #2563eb;
}
.header-icon__spin {
  animation: header-spin 1.6s linear infinite;
}
.header-icon__warn-dot {
  position: absolute;
  top: 7px;
  right: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dc2626;
  border: 1px solid #fff;
}
@keyframes header-spin {
  to {
    transform: rotate(360deg);
  }
}
:deep(.el-badge__content.is-fixed) {
  top: 4px;
  right: 8px;
  transform: translate(50%, -50%) scale(0.85);
  transform-origin: 100% 0%;
}

/* ---------------- User menu ---------------- */
.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
}
.user-trigger:hover {
  background: #f1f5f9;
}
.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.user-name {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

/* ---------------- Minimum-viewport gate ---------------- */
.mobile-gate {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
}
.mobile-gate__card {
  max-width: 420px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}
.mobile-gate__icon {
  color: #2563eb;
  margin-bottom: 12px;
}
.mobile-gate__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.mobile-gate__subtitle {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
}

/* ---------------- Mobile shell (≤768px) ---------------- *
 * Collapse the fixed left sidebar into the el-drawer. Without this the 232px
 * sticky aside renders on phones and eats the screen — the bug this feature
 * exists to fix. The drawer itself only mounts when opened (hamburger is
 * v-if="isMobile"), so desktop never sees it. Mirrors useIsMobile's breakpoint. */
@media (max-width: 768px) {
  .app-shell > .app-sidebar {
    display: none;
  }
  .app-header {
    padding: 0 16px;
  }
  .app-content {
    padding: 16px;
  }
}
</style>
